import { FC, useCallback, useEffect, useMemo } from 'react';
import { useEffectOnce } from 'react-use';
import { useNavigate, useParams } from 'react-router-dom';
import cx from 'classnames';
import { API } from '@xbto/api-client';
import { PreventNavigation } from '~/hoc/prevent-navigation';
import { DataStore } from '~/store';
import {
  Button,
  CtaBanner,
  CurrencyIdentity,
  FundContactForm,
  FundFacts,
  FundPerformanceTable,
  PageContainer,
  useModal,
  FundFactSheets,
  useBreakpoints,
  PerformanceSettings,
  FundGraph,
  NavBar,
  NavBarButtons,
  BreadcrumbList,
} from '@xbto/universal-components';
import { useTrackEvent } from 'common';
import { saveBlobAsFile } from '~/utils/save-blob-as-file';
import { SectionActivity } from './section-activity';
import { AppLoader } from '~/components/app-loader';
import { APP_ROUTES } from '~/routes';
import { intervalToDuration } from 'date-fns';

export const AccountHolding: FC = () => {
  /**
   * Store
   */
  const accessToken = DataStore.useStoreState(s => s.user.accessToken);
  const accountDetail = DataStore.useStoreState(s => s.portfolio.accountDetail);
  const currencyCodes = DataStore.useStoreState(s => s.metaData.currencyCodes);
  const fundAssetBalanceHistory = DataStore.useStoreState(
    s => s.holding.fundAssetBalanceHistory
  );
  const funds = DataStore.useStoreState(s => s.markets.funds);
  const fundPerformances = DataStore.useStoreState(
    s => s.holding.fundPerformances
  );
  const fundPerformancesError = DataStore.useStoreState(
    s => s.holding.fundPerformancesError
  );
  const fundFactsheets = DataStore.useStoreState(s => s.holding.fundFactsheets);
  const fundFactsheetsError = DataStore.useStoreState(
    s => s.holding.fundFactsheetsError
  );
  const latestFundFactsheet = DataStore.useStoreState(
    s => s.holding.latestFundFactsheet
  );
  const fundFacts = DataStore.useStoreState(s => s.holding.fundFacts);
  const paginator = DataStore.useStoreState(
    s => s.transactions.paginatedActivities
  );

  const identity = DataStore.useStoreState(s => s.user.identity);
  const relationshipManager = DataStore.useStoreState(
    s => s.settings.relationshipManager
  );

  const resetStore = DataStore.useStoreActions(a => a.holding.resetStore);
  const getFundAssetBalanceHistory = DataStore.useStoreActions(
    a => a.holding.getFundAssetBalanceHistory
  );
  const getFundPerformances = DataStore.useStoreActions(
    a => a.holding.getFundPerformances
  );
  const getFundFactsheets = DataStore.useStoreActions(
    a => a.holding.getFundFactsheets
  );
  const getFundFactsheetDownloadUrl = DataStore.useStoreActions(
    a => a.holding.getFundFactsheetDownloadUrl
  );
  const getFundFacts = DataStore.useStoreActions(a => a.holding.getFundFacts);
  const getFunds = DataStore.useStoreActions(a => a.markets.getFunds);
  const getAccountDetail = DataStore.useStoreActions(
    _ => _.portfolio.getAccountDetail
  );

  /**
   * Hooks
   */
  const { currencyCode = '', id: accountId = '' } = useParams();
  const trackEvent = useTrackEvent();
  const { breakpoints } = useBreakpoints(500);
  const { Modal: InvestNowModal, open, close } = useModal();
  const navigate = useNavigate();

  const accountDetailAsset = useMemo(() => {
    return accountDetail?.assets.find(a => a.currency.code === currencyCode);
  }, [currencyCode, accountDetail]);
  const isUsdFund = useMemo(() => {
    if (!accountDetailAsset) {
      return false;
    }
    const secondaryAssetCode =
      accountDetailAsset?.fundDetails?.secondaryAsset?.code || undefined;
    if (!secondaryAssetCode) {
      return false;
    }
    if (secondaryAssetCode?.toUpperCase() !== 'USD') {
      return false;
    }
    return true;
  }, [accountDetailAsset]);

  const validate = useCallback(() => {
    const hasCcy = currencyCodes.includes(currencyCode);
    return currencyCode && hasCcy;
  }, [currencyCode, currencyCodes]);

  useEffect(() => {
    return () => {
      resetStore();
    };
  }, [resetStore]);

  useEffect(() => {
    if (accountId && !accountDetail) {
      getAccountDetail({
        accountId,
      });
    }
  }, [accountId, accountDetail, getAccountDetail]);
  useEffect(() => {
    if (currencyCode && accountId && accountDetailAsset) {
      let isLessThan1Year = false;

      if (!!accountDetailAsset?.fundDetails?.firstSubscriptionDate) {
        isLessThan1Year =
          intervalToDuration({
            start: new Date(
              accountDetailAsset.fundDetails.firstSubscriptionDate.toString()
            ),
            end: new Date(),
          }).years === 0;
      }

      if (
        isLessThan1Year &&
        !!accountDetailAsset?.fundDetails?.firstSubscriptionDate
      ) {
        getFundAssetBalanceHistory({
          asset: currencyCode,
          accountId,
          inceptionDate: new Date(
            accountDetailAsset.fundDetails.firstSubscriptionDate.toString()
          ),
        });
      } else {
        getFundAssetBalanceHistory({
          asset: currencyCode,
          accountId,
        });
      }

      getFundPerformances({
        asset: currencyCode,
        accountId,
        type: API.MonthlyFundPerformanceAssetType.InKind,
      });
      getFundFactsheets({
        asset: currencyCode,
        accountId,
        year: null,
      });
      getFundFacts({
        asset: currencyCode,
        accountId,
      });
    }
  }, [
    accountDetailAsset,
    accountId,
    currencyCode,
    getFundAssetBalanceHistory,
    getFundFacts,
    getFundFactsheets,
    getFundPerformances,
  ]);

  const handleOnTimeRangeChanged = useCallback(
    timeRange => {
      getFundAssetBalanceHistory({
        asset: currencyCode,
        accountId,
        timeRange,
      });
    },
    [accountId, currencyCode]
  );

  const handlePressDownloadLatest = useCallback(() => {
    trackEvent('download-factsheet_cta_clicked', {
      source: 'funds-graph_download-latest-factsheet',
    });

    if (accountId && latestFundFactsheet) {
      getFundFactsheetDownloadUrl({
        asset: currencyCode,
        accountId,
        year: null,
        month: null,
        downloadCallback: (blob: Blob, filename: string) => {
          saveBlobAsFile(blob, filename);
        },
      });
    }
  }, [
    accountId,
    currencyCode,
    getFundFactsheetDownloadUrl,
    latestFundFactsheet,
    trackEvent,
  ]);

  const handlePerformanceInPress = useCallback(
    (val: PerformanceSettings) => {
      getFundPerformances({
        asset: currencyCode,
        accountId,
        type:
          val === PerformanceSettings.ASSET_ACCUMULATION
            ? API.MonthlyFundPerformanceAssetType.InKind
            : API.MonthlyFundPerformanceAssetType.Usd,
      });
    },
    [accountId, currencyCode, getFundPerformances]
  );

  const handleFactsheetYearChange = useCallback(
    (year: number) => {
      if (accountId) {
        getFundFactsheets({
          asset: currencyCode,
          accountId,
          year,
        });
      }
    },
    [accountId, currencyCode, getFundFactsheets]
  );

  const handleFactSheetDownloadPress = useCallback(
    (month: number, year: number) => {
      trackEvent('download-factsheet_cta_clicked', {
        source: 'funds-graph_monthly-icon',
        month,
        year,
      });
      if (accountId) {
        getFundFactsheetDownloadUrl({
          asset: currencyCode,
          accountId,
          year,
          month,
          downloadCallback: (blob: Blob, filename: string) => {
            saveBlobAsFile(blob, filename);
          },
        });
      }
    },
    [accountId, currencyCode, getFundFactsheetDownloadUrl, trackEvent]
  );

  const handlePressBack = useCallback(() => {
    const url =
      window.location.pathname !==
      window.location.href.split(window.location.origin)[1]
        ? APP_ROUTES.AUTH_PORTFOLIO
        : -1;

    navigate(url as APP_ROUTES);
  }, [navigate]);

  const goToFundsAccount = useCallback(() => {
    navigate(APP_ROUTES.AUTH_PORTFOLIO_ACCOUNT.replace(':id', accountId));
  }, [accountId, navigate]);

  const handleBeforeInvestSubmit = useCallback(() => {
    trackEvent('contact-form_send-cta_clicked', {
      source: 'invest_flow',
      currencyCode: currencyCode,
    });
  }, [currencyCode, trackEvent]);

  const handleBeforeRedeemSubmit = useCallback(() => {
    trackEvent('contact-form_send-cta_clicked', {
      source: 'redeem_flow',
      currencyCode,
    });
  }, [currencyCode, trackEvent]);

  const trackPerformanceInClicked = useCallback(() => {
    trackEvent('fund-performance-in_toggle_clicked', {
      source: 'investor-mom-performance-table',
    });
  }, [trackEvent]);

  const onPressInvest = useCallback(
    (source: string) => async () => {
      if (identity) {
        trackEvent('fund-subscription_cta_clicked', { source });
        open({
          Body: (
            <FundContactForm
              mode="invest"
              accessToken={accessToken ?? ''}
              fundCode={currencyCode}
              funds={funds ?? []}
              identity={identity}
              manager={relationshipManager}
              onBeforeSubmit={handleBeforeInvestSubmit}
              onClose={close}
            />
          ),
        });
      }
    },
    [
      accessToken,
      currencyCode,
      close,
      funds,
      handleBeforeInvestSubmit,
      identity,
      open,
      relationshipManager,
      trackEvent,
    ]
  );

  const onPressRedeem = useCallback(async () => {
    if (identity) {
      trackEvent('fund-redemption_cta_clicked');
      open({
        Body: (
          <FundContactForm
            mode="redeem"
            accessToken={accessToken ?? ''}
            fundCode={currencyCode}
            funds={funds ?? []}
            identity={identity}
            manager={relationshipManager}
            onBeforeSubmit={handleBeforeRedeemSubmit}
            onClose={close}
          />
        ),
      });
    }
  }, [
    accessToken,
    currencyCode,
    close,
    funds,
    handleBeforeRedeemSubmit,
    identity,
    open,
    relationshipManager,
    trackEvent,
  ]);

  const redeemCta = useMemo(() => {
    return (
      <Button
        key="redeem"
        label="Redeem"
        variant="tertiary"
        size="sm"
        onPress={onPressRedeem}
      />
    );
  }, [onPressRedeem]);

  const subscribeCta = useMemo(() => {
    return (
      <Button
        key="subscribe"
        label="Subscribe more"
        variant="primary"
        size="sm"
        onPress={onPressInvest('funds-graph_subscribe-more')}
      />
    );
  }, [onPressInvest]);

  /**
   * Effects
   */
  useEffectOnce(() => {
    getFunds();
  });

  /**
   * DOM
   */
  if (!accountDetail || !accountDetailAsset) {
    return (
      <div className="flex-1">
        <AppLoader isFixed spinnerTop="104px" />
      </div>
    );
  }
  const horizontalSeparator = (
    <hr
      className={cx(
        breakpoints.LESS_THAN_M && '-mx-4',
        breakpoints.MORE_THAN_M && '-mx-6'
      )}
    />
  );

  return (
    <PreventNavigation validate={validate}>
      <div className={cx(breakpoints.MORE_THAN_M ? 'block' : 'hidden')}>
        <NavBar onPressBack={handlePressBack}>
          <BreadcrumbList
            items={[
              { label: 'Funds', onPress: goToFundsAccount },
              { label: accountDetailAsset.currency.name },
            ]}
          />
          <NavBarButtons>
            {redeemCta}
            {subscribeCta}
          </NavBarButtons>
        </NavBar>
      </div>
      <PageContainer>
        <div className="pt-6">
          <div className="flex flex-col flex-1 gap-6 mb-6">
            <CurrencyIdentity
              code={accountDetailAsset.currency.code}
              displayCode={null}
              name={accountDetailAsset.currency.name}
              size="lg"
            />
            <FundGraph
              accountDetailAsset={accountDetailAsset}
              data={fundAssetBalanceHistory}
              onTimeRange={handleOnTimeRangeChanged}
              header={
                breakpoints.LESS_THAN_M && (
                  <div className="flex flex-row items-center gap-2">
                    {subscribeCta}
                    {redeemCta}
                  </div>
                )
              }
            />

            <CtaBanner
              title={`Invest more into ${accountDetailAsset.currency.name}`}
              text="Top-up your investment quickly and easily here"
              variant="success"
              ctaLabel="Subscribe more"
              onCtaPress={onPressInvest('funds-holdings-page_invest-now')}
            />
          </div>
          {(!!fundPerformances?.length || fundPerformancesError) && (
            <div className={cx(breakpoints.LESS_THAN_M ? 'py-8' : 'py-12')}>
              <FundPerformanceTable
                data={fundPerformances}
                error={fundPerformancesError}
                onPerformanceInPress={handlePerformanceInPress}
                trackPerformanceInClicked={trackPerformanceInClicked}
                hidePerformanceInSelector={isUsdFund}
              />
            </div>
          )}
          {!paginator?.result.length && paginator?.page === 1 ? null : (
            <>
              {horizontalSeparator}
              <div className={cx(breakpoints.LESS_THAN_M ? 'py-8' : 'py-12')}>
                <SectionActivity currencyCode={currencyCode} />
              </div>
            </>
          )}

          {!!fundFactsheets?.years?.length && (
            <>
              {horizontalSeparator}
              <div className={cx(breakpoints.LESS_THAN_M ? 'py-8' : 'py-12')}>
                <FundFactSheets
                  rowData={fundFactsheets?.factSheets}
                  error={fundFactsheetsError}
                  years={fundFactsheets?.years}
                  onYearChange={handleFactsheetYearChange}
                  onDownloadLatestPress={handlePressDownloadLatest}
                  onFactSheetDownloadPress={handleFactSheetDownloadPress}
                />
              </div>
            </>
          )}
          {!!fundFacts && (
            <>
              {horizontalSeparator}
              <div className={cx(breakpoints.LESS_THAN_M ? 'py-8' : 'py-12')}>
                <FundFacts data={fundFacts} />
              </div>
            </>
          )}
        </div>
        <InvestNowModal />
      </PageContainer>
    </PreventNavigation>
  );
};
